import {memo, useCallback} from 'react'
import {useLocalParticipant, useMediaDeviceSelect, useRoomContext} from '@livekit/components-react'
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  ScreenShare,
  MessageSquare,
  Circle,
  Square,
} from 'lucide-react'
import {ActiveSpeakers} from './active-speakers'
import {MediaToggleSplitButton} from '@/pages/room/device-selector'
import {
  useAudioSettings,
  useApplyNoiseGateToLocalMic,
  AudioSettingsPopover,
} from '@/pages/room/audio-settings'
import {useRecording} from '@/pages/room/room-recording'
import {useAuthStore} from '@/features/auth'
import {IconButton} from '@/shared/ui'
import {STORAGE_KEYS} from '@/shared/config'
import {cn} from '@/shared/lib/cn'
import {isScreenShareSupported} from './screenShareSupport'

interface RoomControlsBarProps {
  roomId: string
  isChatOpen?: boolean
  unreadCount?: number
  onToggleChat?: () => void
}

export const RoomControlsBar = memo(function RoomControlsBar({
  roomId,
  isChatOpen = false,
  unreadCount = 0,
  onToggleChat,
}: RoomControlsBarProps) {
  const {threshold, setThreshold, mode, setMode, processor} = useAudioSettings()
  useApplyNoiseGateToLocalMic(processor)

  const room = useRoomContext()
  const {localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled} =
    useLocalParticipant()

  const {
    devices: audioDevices,
    activeDeviceId: activeAudioDeviceId,
    setActiveMediaDevice: switchAudioDevice,
  } = useMediaDeviceSelect({kind: 'audioinput', room})

  const {
    devices: videoDevices,
    activeDeviceId: activeVideoDeviceId,
    setActiveMediaDevice: switchVideoDevice,
  } = useMediaDeviceSelect({kind: 'videoinput', room})
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const {status, startRecording, lastError, clearError, isRecordingByOthers} = useRecording(roomId)

  const handleRecordingToggle = useCallback(() => {
    clearError()
    if (status === 'recording') {
      return
    }
    if (status === 'idle') {
      void startRecording().catch(err => {
        console.error(
          '[RoomControlsBar] Start recording failed:',
          err instanceof Error ? err.message : err,
        )
      })
    }
  }, [status, startRecording, clearError])

  const handleToggleAudio = useCallback(() => {
    localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)
  }, [localParticipant, isMicrophoneEnabled])

  const handleToggleVideo = useCallback(() => {
    localParticipant.setCameraEnabled(!isCameraEnabled)
  }, [localParticipant, isCameraEnabled])

  const handleToggleScreenShare = useCallback(() => {
    if (isScreenShareEnabled) {
      void localParticipant.setScreenShareEnabled(false)
    } else {
      void localParticipant
        .setScreenShareEnabled(true, {
          audio: true,
          systemAudio: 'include',
        })
        .catch(err => {
          console.warn('[RoomControlsBar] Screen share with audio failed:', err)
        })
    }
  }, [localParticipant, isScreenShareEnabled])

  const handleSelectAudioDevice = useCallback(
    (deviceId: string) => {
      localStorage.setItem(STORAGE_KEYS.AUDIO_DEVICE_ID, deviceId)
      void switchAudioDevice(deviceId)
    },
    [switchAudioDevice],
  )

  const handleSelectVideoDevice = useCallback(
    (deviceId: string) => {
      localStorage.setItem(STORAGE_KEYS.VIDEO_DEVICE_ID, deviceId)
      void switchVideoDevice(deviceId)
    },
    [switchVideoDevice],
  )

  return (
    <div className="flex flex-col items-stretch gap-3 sm:py-3 pb-4 pt-2 px-4 sm:bg-muted/80 sm:rounded-xl shrink-0 sm:flex-row sm:items-center sm:gap-4">
      <ActiveSpeakers className="hidden sm:block" />
      <div className="flex items-center gap-2 justify-center w-full sm:justify-start sm:w-auto">
        <MediaToggleSplitButton
          isEnabled={isMicrophoneEnabled}
          onToggle={handleToggleAudio}
          devices={audioDevices}
          selectedDeviceId={activeAudioDeviceId}
          onSelectDevice={handleSelectAudioDevice}
          icon={<Mic />}
          disabledIcon={<MicOff />}
          toggleLabel={isMicrophoneEnabled ? 'Выключить микрофон' : 'Включить микрофон'}
          pickerLabel="Выбрать микрофон"
          deviceFallbackName="Микрофон"
          size="small"
        />
        <MediaToggleSplitButton
          isEnabled={isCameraEnabled}
          onToggle={handleToggleVideo}
          devices={videoDevices}
          selectedDeviceId={activeVideoDeviceId}
          onSelectDevice={handleSelectVideoDevice}
          icon={<Video />}
          disabledIcon={<VideoOff />}
          toggleLabel={isCameraEnabled ? 'Выключить камеру' : 'Включить камеру'}
          pickerLabel="Выбрать камеру"
          deviceFallbackName="Камера"
          size="small"
        />
        {isScreenShareSupported() && (
          <IconButton
            variant={isScreenShareEnabled ? 'active' : 'default'}
            size="small"
            onClick={handleToggleScreenShare}
            aria-label={
              isScreenShareEnabled ? 'Остановить демонстрацию экрана' : 'Поделиться экраном'
            }
            title={isScreenShareEnabled ? 'Остановить демонстрацию экрана' : 'Поделиться экраном'}
          >
            <ScreenShare />
          </IconButton>
        )}
        {isAuthenticated && !isRecordingByOthers && (
          <IconButton
            variant={status === 'recording' ? 'active' : 'default'}
            size="small"
            onClick={handleRecordingToggle}
            disabled={status === 'starting' || status === 'stopping' || status === 'recording'}
            aria-label={
              status === 'recording'
                ? 'Идёт запись (остановится при выходе всех)'
                : status === 'idle'
                  ? 'Начать запись'
                  : 'Запись'
            }
            title={
              lastError
                ? `Ошибка: ${lastError}`
                : status === 'recording'
                  ? 'Идёт запись. Остановится при выходе всех участников.'
                  : status === 'idle'
                    ? 'Начать запись'
                    : 'Запись'
            }
            className={cn(status === 'recording' && 'bg-destructive! hover:bg-destructive/90!')}
          >
            {status === 'recording' ? <Square /> : <Circle />}
          </IconButton>
        )}
        <AudioSettingsPopover
          processor={processor}
          level={isMicrophoneEnabled ? undefined : 0}
          threshold={threshold}
          mode={mode}
          onThresholdChange={setThreshold}
          onModeChange={setMode}
        />
        <div className="relative">
          <IconButton
            variant={isChatOpen ? 'active' : 'default'}
            size="small"
            onClick={onToggleChat}
            aria-label={isChatOpen ? 'Закрыть чат' : 'Открыть чат'}
            title={isChatOpen ? 'Закрыть чат' : 'Открыть чат'}
          >
            <MessageSquare />
          </IconButton>
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 px-0.5 bg-primary rounded-full text-[10px] font-semibold flex items-center justify-center text-primary-foreground leading-none pointer-events-none select-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  )
})
